'use client';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCard } from './blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useState } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

export const BlogsList = ({ locale }: { locale: string }) => {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(true);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      setIsLoading(true);
      return await fetchBlogs({
        locale,
        query: {
          orderBy: 'created_at',
          order: 'desc',
          page: pageParam,
          results: 8,
        },
      }).then(data => {
        setIsLoading(false);
        return data;
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {blogs.map((blog: Blog) => (
        <BlogCard blog={blog} key={blog.slug} />
      ))}
      {isLoading && (
        <div className="col-span-4 flex items-center justify-center py-20">
          <Spinner className="h-16 w-16 text-primary" />
        </div>
      )}
      <div
        className={cn(
          'col-span-1 mt-12 flex w-full items-center justify-center sm:col-span-2 lg:col-span-3 xl:col-span-4',
          {
            hidden: !blogsQuery.hasNextPage,
          },
        )}
      >
        <Button
          onClick={() => blogsQuery.fetchNextPage()}
          isLoading={blogsQuery.isFetchingNextPage}
        >
          {t('text338')}
        </Button>
      </div>
    </div>
  );
};
